import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { TextInput } from "Components/_Shared/Inputs/TextInput";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlRadioButtons } from "Forms/Components/BlRadioButtons";
import { DatePicker } from "Forms/Components/DatePicker";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { FormSwitch } from "Forms/Components/FormSwitch";
import { type HousingPropertyGoalFormType, getHousingPropertyFormSchema } from "Forms/Schemas/Goals/housingPropertyFormSchema";
import { minDate } from "Forms/Schemas/Goals/housingPropertyFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetHousingPropertyGoalQueryKey,
  useCreateHousingPropertyGoal,
  useGetAnalysisClients,
  useUpdateHousingPropertyGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  AnalysisClientItemRelationType,
  GoalOverviewSubType,
  type HousingPropertyGoalDto,
  HousingPropertyGoalDtoPriorityLevel,
  TimeRangeType,
} from "Infrastructure/ApiZfp/Schema";
import {
  DEFAULT_FEE_RATE,
  DEFAULT_INFLATION_RATE,
  DEFAULT_LOAN_DURATION,
  DEFAULT_LTV,
  INTEREST_RATE_CONSERVATIVE,
  INTEREST_RATE_MORTGAGE,
} from "Infrastructure/Constants/financialConstants";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { addMonths, differenceInYears, format, isBefore, startOfDay, startOfMonth } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useHousingPropertyCalculations } from "../Hooks/useHousingPropertyCalculations";
import { ModelingAccordion } from "./ModelingAccordion";
import { ModelingHint } from "./ModelingHint";
import { MortgageComparisonTable } from "./MortgageComparisonTable";
import { PropertyPurchaseChart } from "./PropertyPurchaseChart";

type Props = {
  readonly onClose: () => void;
  readonly onSubTypeChange: (goalSubType: GoalOverviewSubType) => void;
  readonly editGoal?: HousingPropertyGoalDto;
  readonly goalId?: string;
};

export const HousingPropertyGoalForm = forwardRef<{}, Props>(({ onSubTypeChange, onClose, editGoal, goalId }, ref) => {
  const { t } = useResource();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const { data: clientRelationsData } = useGetAnalysisClients(analysisId, versionId);
  const client = clientRelationsData?.clients.find((client) => client.relationType === AnalysisClientItemRelationType.Client);
  const birthDate = client?.birthDate;

  const methods = useForm<HousingPropertyGoalFormType>({
    resolver: yupResolver(getHousingPropertyFormSchema(t)),
    mode: "onBlur",
    defaultValues: editGoal
      ? {
          name: editGoal.name,
          includeInflation: !!editGoal.modeling.inflation?.enabled,
          includeEntryFee: !!editGoal.modeling.fee?.enabled,
          inflationRate: editGoal.modeling.inflation?.rate ?? 0,
          feeRate: editGoal.modeling.fee?.rate ?? 0,
          interestRate: editGoal.modeling.appreciationRate?.toString() as any,
          goalSubType: GoalOverviewSubType.housing_property,
          currentSavingsAmount: editGoal.data.currentSavings?.value.toString() as any,
          downpaymentAmount: editGoal.data.targetSavings?.value.toString() as any,
          expectedPriceAmount: editGoal.data.expectedPurchaseAmount?.value.toString() as any,
          loanAmount: editGoal.data.loanAmount?.value.toString() as any,
          loanDuration: editGoal.modeling.mortgageTerm?.value.toString() as any,
          loanInterestRate: editGoal.modeling.interestRate?.toString() as any,
          loanToValue: editGoal.data.loanToValueRatio?.toString() as any,
          targetDate: (editGoal.data.targetDate && new Date(editGoal.data.targetDate)) || new Date(),
          priority: editGoal.priorityLevel === HousingPropertyGoalDtoPriorityLevel.high,
          investCurrentSavings: !!editGoal.modeling.investCurrentSavings,
          note: editGoal.note || "",
          referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
        }
      : {
          includeInflation: true,
          includeEntryFee: true,
          investCurrentSavings: true,
          inflationRate: DEFAULT_INFLATION_RATE.toString() as any,
          interestRate: INTEREST_RATE_CONSERVATIVE.toString() as any,
          feeRate: DEFAULT_INFLATION_RATE.toString() as any,
          goalSubType: GoalOverviewSubType.housing_property,
          loanDuration: DEFAULT_LOAN_DURATION,
          loanInterestRate: INTEREST_RATE_MORTGAGE.toString() as any,
          loanToValue: DEFAULT_LTV.toString() as any,
          name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingProperty),
          referenceStartDate: new Date(),
        },
  });

  const {
    control,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
    handleSubmit,
  } = methods;

  const queryClient = useQueryClient();

  const { mutate: createHousingPropertyGoal } = useCreateHousingPropertyGoal();

  const { mutate: updateHousingPropertyGoal } = useUpdateHousingPropertyGoal();

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (editGoal) {
        handleSubmit(onEditGoal, (data) => console.log(data))();
      } else {
        handleSubmit(onCreateGoal)();
      }
    },
  }));

  const currentSavingsAmount = watch("currentSavingsAmount");
  const downpaymentAmount = watch("downpaymentAmount");
  const expectedPriceAmount = watch("expectedPriceAmount");
  const includeEntryFee = watch("includeEntryFee");
  const includeInflation = watch("includeInflation");
  const inflationRate = watch("inflationRate");
  const interestRate = watch("interestRate");
  const loanAmount = watch("loanAmount");
  const loanDuration = watch("loanDuration");
  const loanInterestRate = watch("loanInterestRate");
  const loanToValue = watch("loanToValue");
  const targetDate = watch("targetDate");
  const investCurrentSavings = watch("investCurrentSavings");
  const referenceStartDate = watch("referenceStartDate");

  const clientAge = differenceInYears(referenceStartDate, birthDate ? new Date(birthDate) : referenceStartDate);
  const isInvalidTargetDate = isBefore(startOfDay(targetDate), startOfDay(minDate));
  const {
    calculations: {
      hasEnoughMoney,
      overpayment,
      toSaveUp,
      loanTotalAmount,
      interestPaid,
      loanPaymentAmount,
      monthlyContribution,
      monthlyContributionWithFee,
    },
    handlers: { handleExpectedPriceChange, handleDownpaymentChange, handleLoanAmountChange, handleLtvChange },
  } = useHousingPropertyCalculations({ watch, setValue });

  const shouldRenderChart =
    currentSavingsAmount >= 0 &&
    downpaymentAmount >= 0 &&
    expectedPriceAmount > 0 &&
    loanAmount > 0 &&
    loanDuration > 0 &&
    loanInterestRate >= 0 &&
    loanToValue > 0 &&
    monthlyContribution &&
    loanPaymentAmount &&
    targetDate &&
    !isInvalidTargetDate &&
    !hasEnoughMoney &&
    clientAge < 71;

  useEffect(() => {
    if (!targetDate || isInvalidTargetDate) return;
    clearErrors("targetDate");
  }, [targetDate, isInvalidTargetDate, clearErrors]);

  const onCreateGoal = (data: HousingPropertyGoalFormType) => {
    createHousingPropertyGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? HousingPropertyGoalDtoPriorityLevel.high : HousingPropertyGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.targetDate, "yyyy-MM-dd"),
            currentSavings: { value: data.currentSavingsAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            targetSavings: { value: data.downpaymentAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            expectedPurchaseAmount: { value: data.expectedPriceAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            loanAmount: { value: data.loanAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            remainingSavings: { value: toSaveUp, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            loanToValueRatio: data.loanToValue,
          },
          modeling: {
            interestRate: data.loanInterestRate,
            investmentAmount: { value: monthlyContributionWithFee, currencyCode: "CZK", frequency: AmountFrequency.monthly },
            mortgageTerm: { value: data.loanDuration, type: TimeRangeType.year },
            loanPayment: { value: loanPaymentAmount, currencyCode: "CZK", frequency: AmountFrequency.monthly },
            investCurrentSavings: true,
            totalAmount: { value: loanTotalAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            inflation: { enabled: !!includeInflation, rate: data.inflationRate },
            fee: { enabled: !!includeEntryFee, rate: data.feeRate },
            overpaymentRate: overpayment,
            totalInterestPaid: interestPaid,
            appreciationRate: data.interestRate,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEditGoal = (data: HousingPropertyGoalFormType) => {
    updateHousingPropertyGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? HousingPropertyGoalDtoPriorityLevel.high : HousingPropertyGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.targetDate, "yyyy-MM-dd"),
            currentSavings: { value: data.currentSavingsAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            expectedPurchaseAmount: { value: data.expectedPriceAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            targetSavings: { value: data.downpaymentAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            loanAmount: { value: data.loanAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            remainingSavings: { value: toSaveUp, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            loanToValueRatio: data.loanToValue,
          },
          modeling: {
            interestRate: data.loanInterestRate,
            mortgageTerm: { value: data.loanDuration, type: TimeRangeType.year },
            loanPayment: { value: loanPaymentAmount, currencyCode: "CZK", frequency: AmountFrequency.monthly },
            appreciationRate: data.interestRate,
            investCurrentSavings: data.investCurrentSavings,
            investmentAmount: { value: monthlyContributionWithFee, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            totalAmount: { value: loanTotalAmount, currencyCode: "CZK", frequency: AmountFrequency.one_time },
            inflation: { enabled: !!includeInflation, rate: data.inflationRate },
            fee: { enabled: !!includeEntryFee, rate: data.feeRate },
            overpaymentRate: overpayment,
            totalInterestPaid: interestPaid,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetHousingPropertyGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const renderHint = () => {
    if (hasEnoughMoney) {
      return (
        <ModelingHint title={t(Resources.AnalysisDetail.Goals.HasEnoughMoneyHint.Title)}>
          {t(Resources.AnalysisDetail.Goals.HasEnoughMoneyHint.Content)}
        </ModelingHint>
      );
    }

    if (clientAge >= 60 && clientAge < 71) {
      return (
        <ModelingHint title={t(Resources.AnalysisDetail.Goals.QuiteOldHint.Title)}>
          <Typography variant="body2">
            {t(Resources.AnalysisDetail.Goals.QuiteOldHint.FirstPhrase)}{" "}
            <Typography variant="body2" display="inline" fontWeight={500}>
              60 {t(Resources.Common.Years)}
            </Typography>{" "}
            {t(Resources.AnalysisDetail.Goals.QuiteOldHint.SecondPhrase)}
          </Typography>
        </ModelingHint>
      );
    }

    if (clientAge >= 71) {
      return (
        <ModelingHint title={t(Resources.AnalysisDetail.Goals.TooOldHint.Title)}>
          <Typography variant="body2">
            {t(Resources.AnalysisDetail.Goals.TooOldHint.FirstPhrase)}{" "}
            <Typography variant="body2" display="inline" fontWeight={500}>
              71 {t(Resources.Common.Years)}
            </Typography>{" "}
            {t(Resources.AnalysisDetail.Goals.TooOldHint.SecondPhrase)}
          </Typography>
        </ModelingHint>
      );
    }
  };

  const renderChart = () => {
    if (shouldRenderChart) {
      return (
        <Grid size={12}>
          <Box padding={Spacing.S} display="flex" justifyContent="space-around" alignItems="center">
            <Stack spacing={Spacing.XS} display="flex" justifyContent="center" alignItems="center">
              <Typography variant="caption" fontWeight={500}>
                {t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
              </Typography>
              <Typography variant="caption">
                {formatCurrency(Math.round(Number(monthlyContributionWithFee)), CurrencyCode.CZK)}/{t(Resources.Common.Month)}
              </Typography>
            </Stack>
            <Stack spacing={Spacing.XS} display="flex" justifyContent="center" alignItems="center">
              <Typography variant="caption" fontWeight={500}>
                {t(Resources.AnalysisDetail.Goals.LoanPayment)}
              </Typography>
              <Typography variant="caption">
                {formatCurrency(Math.round(Number(loanPaymentAmount)), CurrencyCode.CZK)}/{t(Resources.Common.Month)}
              </Typography>
            </Stack>
          </Box>
          <PropertyPurchaseChart
            investCurrentSavings={investCurrentSavings}
            currentSavingsAmount={Number(currentSavingsAmount)}
            downpaymentAmount={Number(downpaymentAmount)}
            inflationRate={Number(inflationRate)}
            interestRate={interestRate ? Number(interestRate) : 0}
            loanAmount={Number(loanAmount)}
            loanDuration={Number(loanDuration)}
            monthlyContributionAmount={Number(monthlyContribution)}
            targetDate={targetDate}
            loanInterestRate={Number(loanInterestRate)}
            referenceStartDate={referenceStartDate}
          />
        </Grid>
      );
    }
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.Housing)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <BlCheckboxInput
                  control={control}
                  disabled={readOnly}
                  name="priority"
                  text={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  errors={errors}
                />
              </Grid>
              <Grid size={12}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  errors={errors}
                  labelEndAdornment={<StyledLabelAdornment />}
                />
              </Grid>
              <Grid size={12}>
                <BlRadioButtons
                  control={control}
                  disabled={!!editGoal || readOnly}
                  name="goalSubType"
                  label={t(Resources.AnalysisDetail.Goals.WhatAreYouPlanning)}
                  onChange={(goalSubType) => onSubTypeChange(goalSubType as GoalOverviewSubType)}
                  codeList={[
                    {
                      code: GoalOverviewSubType.housing_property,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingProperty),
                    },
                    {
                      code: GoalOverviewSubType.housing_mortgage,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingMortgage),
                    },
                    {
                      code: GoalOverviewSubType.housing_other,
                      name: t(Resources.AnalysisDetail.Goals.GoalSubType.HousingOther),
                    },
                  ]}
                  errors={errors}
                />
              </Grid>
              <Grid size={6}>
                <DatePicker
                  control={control}
                  disabled={readOnly}
                  name="targetDate"
                  label={t(Resources.AnalysisDetail.Goals.PurchaseDate)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  minDate={startOfMonth(addMonths(new Date(), 1))}
                  errors={errors}
                />
              </Grid>
              <Grid size={6}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="currentSavingsAmount"
                  label={t(Resources.AnalysisDetail.Goals.CurrentSavingsAmount)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 0,
                      min: 0,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                />
              </Grid>
              <Grid size={5}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="expectedPriceAmount"
                  label={t(Resources.AnalysisDetail.Goals.ExpectedPriceAmount)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 0,
                      min: 100,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                  onChange={handleExpectedPriceChange}
                />
              </Grid>
              <Grid size={2}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="loanToValue"
                  label={t(Resources.Common.LTV)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  maxLength={2}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      %
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                      min: 0,
                    },
                  ]}
                  onChange={handleLtvChange}
                />
              </Grid>
              <Grid size={5}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="downpaymentAmount"
                  label={t(Resources.AnalysisDetail.Goals.Downpayment)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 0,
                      min: 0,
                      max: expectedPriceAmount,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                  onChange={handleDownpaymentChange}
                />
              </Grid>
              <Grid size={6}>
                <BlFormInput
                  control={control}
                  disabled={readOnly}
                  name="loanAmount"
                  label={t(Resources.AnalysisDetail.Goals.LoanAmount)}
                  labelEndAdornment={<StyledLabelAdornment />}
                  errors={errors}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      min: 0,
                      max: expectedPriceAmount,
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                  onChange={handleLoanAmountChange}
                />
              </Grid>
              <Grid size={6}>
                <TextInput
                  label={t(Resources.AnalysisDetail.Goals.NeededToSaveUp)}
                  disabled
                  value={toSaveUp}
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                    },
                  ]}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                />
              </Grid>
              {renderHint()}
            </Grid>
          </Box>
          {targetDate &&
            currentSavingsAmount >= 0 &&
            expectedPriceAmount > 0 &&
            loanToValue > 0 &&
            downpaymentAmount >= 0 &&
            loanAmount > 0 &&
            !hasEnoughMoney &&
            clientAge < 71 && (
              <Box>
                <ModelingAccordion>
                  <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                    <Grid size={5}>
                      <BlFormInput
                        control={control}
                        disabled={readOnly}
                        name="loanInterestRate"
                        label={t(Resources.AnalysisDetail.Goals.LoanInterestRate)}
                        labelEndAdornment={<StyledLabelAdornment />}
                        errors={errors}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            %
                          </Typography>
                        }
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            min: 0,
                            scale: 2,
                          },
                        ]}
                      />
                    </Grid>
                    <Grid size={2}>
                      <BlFormInput
                        control={control}
                        disabled={readOnly}
                        name="loanDuration"
                        label={t(Resources.AnalysisDetail.Goals.LoanDuration)}
                        labelEndAdornment={<StyledLabelAdornment />}
                        errors={errors}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            {t(Resources.Common.Years)}
                          </Typography>
                        }
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 0,
                          },
                        ]}
                      />
                    </Grid>
                    <Grid size={5}>
                      <TextInput
                        label={t(Resources.AnalysisDetail.Goals.LoanPayment)}
                        value={loanPaymentAmount}
                        disabled
                        inputEndAdornment={
                          <Box sx={{ minWidth: 70 }}>
                            <Typography variant="body2" color="secondary">
                              {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                            </Typography>
                          </Box>
                        }
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                          },
                        ]}
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextInput
                        label={t(Resources.AnalysisDetail.Goals.TotalPaid)}
                        disabled
                        unmask="typed"
                        value={loanTotalAmount}
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                          },
                        ]}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            {t(Resources.Common.CZKSuffix)}
                          </Typography>
                        }
                      />
                    </Grid>
                    <Grid size={6}>
                      <BlFormInput
                        control={control}
                        disabled={readOnly}
                        name="interestRate"
                        label={t(Resources.AnalysisDetail.Goals.InterestRate)}
                        labelEndAdornment={<StyledLabelAdornment />}
                        errors={errors}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            %
                          </Typography>
                        }
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 1,
                            min: 0,
                          },
                        ]}
                        onChange={(newInterestRate) => {
                          if (
                            (includeInflation && Number(newInterestRate) === Number(inflationRate)) ||
                            (!includeInflation && Number(newInterestRate) <= 0)
                          ) {
                            setValue("interestRate", interestRate);
                            return;
                          }
                        }}
                      />
                    </Grid>
                    <Grid size={12} display="flex" alignItems="center">
                      <FormSwitch
                        control={control}
                        disabled={readOnly}
                        name="investCurrentSavings"
                        label={t(Resources.AnalysisDetail.Goals.InvestCurrentSavings)}
                        errors={errors}
                      />
                    </Grid>
                    <Grid size={9} display="flex" alignItems="center">
                      <FormSwitch
                        disabled={Number(interestRate) === Number(inflationRate) || readOnly}
                        control={control}
                        name="includeInflation"
                        label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                        onChange={(checked) => {
                          const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;
                          setValue("inflationRate", newInflationRate);
                        }}
                        errors={errors}
                      />
                    </Grid>
                    <Grid size={3}>
                      <BlFormInput
                        disabled={!includeInflation || readOnly}
                        control={control}
                        name="inflationRate"
                        errors={errors}
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            radix: ",",
                            mapToRadix: ["."],
                            min: 0,
                            scale: 1,
                          },
                        ]}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            %
                          </Typography>
                        }
                      />
                    </Grid>
                    <Grid size={9} display="flex" alignItems="center">
                      <FormSwitch
                        control={control}
                        disabled={readOnly}
                        name="includeEntryFee"
                        label={t(Resources.AnalysisDetail.Goals.IncludeEntryFee)}
                        onChange={(checked) => {
                          const newFeeRate = checked ? DEFAULT_FEE_RATE : 0;

                          setValue("feeRate", newFeeRate);
                        }}
                        errors={errors}
                      />
                    </Grid>
                    <Grid size={3}>
                      <BlFormInput
                        disabled={!includeEntryFee || readOnly}
                        control={control}
                        name="feeRate"
                        errors={errors}
                        inputEndAdornment={
                          <Typography variant="body2" color="secondary">
                            %
                          </Typography>
                        }
                        unmask="typed"
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 1,
                            min: 0,
                            max: 20,
                          },
                        ]}
                      />
                    </Grid>
                  </Grid>
                </ModelingAccordion>
              </Box>
            )}
          <Box>
            <Grid container spacing={Spacing.XL}>
              {renderChart()}
              {loanDuration > 0 && loanPaymentAmount > 0 && loanAmount > 0 && loanInterestRate > 0 && (
                <Grid size={12}>
                  <Typography my={Spacing.M} variant="subtitle1" fontWeight={700}>
                    {t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.Comparison)}
                  </Typography>
                  <MortgageComparisonTable
                    loanInterestRate={loanInterestRate}
                    loanAmount={loanAmount}
                    originalLoanDuration={loanDuration}
                    loanPaymentAmount={loanPaymentAmount}
                  />
                </Grid>
              )}
              {loanDuration > 0 && loanPaymentAmount > 0 && (
                <>
                  <Grid size={6}>
                    <TextInput
                      name="overpayment"
                      label={t(Resources.AnalysisDetail.Goals.Overpayment)}
                      disabled
                      value={overpayment}
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          %
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={6}>
                    <TextInput
                      label={t(Resources.AnalysisDetail.Goals.InterestPaid)}
                      disabled
                      value={interestPaid}
                      unmask="typed"
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}
                        </Typography>
                      }
                    />
                  </Grid>
                </>
              )}
              <Grid size={12}>
                <Divider sx={{ marginTop: Spacing.M, marginBottom: Spacing.M }} />
              </Grid>
              <Grid size={12}>
                <BlFormInput
                  disabled={readOnly}
                  control={control}
                  label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                  name="note"
                  errors={errors}
                  multiline
                  minRows={6}
                  maxRows={8}
                  characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
